/**
 * capabilityTransport.ts — how a DESCRIBED capability is actually addressed.
 *
 * `capabilityManifest.ts` reads WHAT the Engine publishes. This module reads
 * HOW to send it, and it derives that from the same published document rather
 * than from a table written here. Nothing in this file names an operation, a
 * query, a parameter or an enum value.
 *
 * WHY THE ENVELOPE IS DERIVED
 * ---------------------------
 * The Engine publishes, per transport, a JSON-Schema fragment describing the
 * envelope. Two facts fall out of it and both are read, never assumed:
 *
 *   • the ADDRESS field is the single required, string-typed envelope property.
 *     A body that cannot say which registered operation it means is not a
 *     request, and an envelope with zero or several candidate address fields is
 *     ambiguous — so it is reported rather than guessed at.
 *   • the PAYLOAD field is the object-typed envelope property. The descriptor's
 *     own `requestSchema` describes that payload, and every other envelope
 *     property is an auxiliary the descriptor may also publish.
 *
 * If the Engine publishes an envelope that fits neither shape, this browser
 * cannot address it, and it says so NAMED, rather than inventing a body. That
 * is the honest outcome: an unserviceable transport is an unavailable
 * capability, and it is indistinguishable from a missing one only if the UI
 * hides it — which it does not.
 *
 * WHAT IS STILL A LOCAL TABLE, AND WHY
 * -----------------------------------
 * Transport id → HTTP route. A route is a transport fact, in the same family as
 * the CSRF header name in `client.ts`: it says where bytes go, not what they
 * mean. The Engine publishes the route in prose inside the transport summary;
 * parsing prose to build requests would be far more fragile than an explicit,
 * tested table.
 *
 * The table is kept honest by an oracle rather than by discipline: every
 * GENERIC transport in the real Engine manifest must either appear here or be
 * reported unavailable by `resolveTransport`. Adding a transport on the Engine
 * side without deciding what this browser does with it turns that suite red.
 */

import type {
  CapabilityDescriptor,
  CapabilityManifest,
  CapabilityTransport,
  JsonSchemaFragment,
} from "./capabilityManifest";

/** Why a published capability cannot be invoked from this browser, if it cannot. */
export const TRANSPORT_NOT_GENERIC = "engine-capability-transport-not-generic";
export const TRANSPORT_UNDECLARED = "engine-capability-transport-undeclared";
export const TRANSPORT_NOT_ADDRESSABLE = "engine-capability-transport-not-addressable";
export const TRANSPORT_NO_ROUTE = "engine-capability-transport-no-route";
/** The Engine published no address VALUE for a capability it says is addressed by one. */
export const TRANSPORT_NO_ADDRESS_VALUE = "engine-capability-transport-no-address-value";

/** The stages of the control loop a transport can support. */
export type ActionPhase = "read" | "preview" | "apply";

export interface TransportPlan {
  /** The Engine's own transport id — reported, never invented. */
  readonly transport: string;
  readonly phases: readonly ActionPhase[];
  /** The envelope property that carries the registered address. */
  readonly addressField: string;
  /** The envelope property that carries the descriptor's payload. */
  readonly payloadField: string;
  /** Envelope properties other than address and payload, when published. */
  readonly auxiliaryFields: readonly string[];
  /** Path templates, already project-scoped. `{changeSetId}` stays a token. */
  readonly paths: Readonly<Record<ActionPhase, string>>;
}

export interface TransportUnavailable {
  readonly transport: string;
  readonly code: string;
  /** Names exactly what the Engine did not publish. */
  readonly missing: string;
  readonly detail: string;
}

export type TransportResolution =
  | { readonly available: true; readonly plan: TransportPlan }
  | { readonly available: false; readonly unavailable: TransportUnavailable };

/**
 * Transport id → route.
 *
 * `preview` and `apply` share one reviewed authority: a mutation accumulates
 * into a change set and only publication writes canonical state. `read` is the
 * deterministic query route, which mutates nothing.
 *
 * `{changeSetId}` is filled by the caller. Nothing else is substituted here.
 */
const ROUTES: ReadonlyMap<string, Readonly<Record<ActionPhase, string>>> = new Map([
  ["query-envelope", { read: "/api/v1/projects/{projectId}/queries", preview: "", apply: "" }],
  ["plan-envelope", {
    read: "",
    preview: "/api/v1/projects/{projectId}/change-sets/{changeSetId}/authoring/preview",
    apply: "/api/v1/projects/{projectId}/change-sets/{changeSetId}/authoring/apply",
  }],
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function propertiesOf(schema: JsonSchemaFragment | undefined): Record<string, JsonSchemaFragment> {
  const raw = schema?.properties;
  return isRecord(raw) ? (raw as Record<string, JsonSchemaFragment>) : {};
}

/**
 * The single required, string-typed property — the address field.
 *
 * Deliberately strict. Two candidate address fields means the browser would be
 * choosing which registered operation the user meant, which is the Engine's
 * decision; zero means there is nothing to address.
 */
function addressFieldOf(envelope: JsonSchemaFragment): string | undefined {
  const properties = propertiesOf(envelope);
  const required = Array.isArray(envelope.required) ? envelope.required : [];
  const candidates = required.filter(name =>
    typeof name === "string" && properties[name]?.type === "string",
  );
  return candidates.length === 1 ? candidates[0] : undefined;
}

/** The object-typed envelope property — the payload the descriptor describes. */
function payloadFieldOf(envelope: JsonSchemaFragment, exclude: string): string | undefined {
  const properties = propertiesOf(envelope);
  const candidates = Object.keys(properties).filter(name =>
    name !== exclude && properties[name]?.type === "object",
  );
  return candidates.length === 1 ? candidates[0] : undefined;
}

function findTransport(manifest: CapabilityManifest, id: string): CapabilityTransport | undefined {
  return manifest.transports.find(transport => transport.id === id);
}

/**
 * Work out how to address one descriptor — or, precisely, why not.
 *
 * Every branch that returns `available: false` names the specific publication
 * that is missing, because "this capability does not work" and "this Engine
 * never published how to address it" are different facts with different
 * remedies and the user has to be able to tell them apart.
 */
export function resolveTransport(manifest: CapabilityManifest, descriptor: CapabilityDescriptor): TransportResolution {
  const id = descriptor.transport;
  const transport = findTransport(manifest, id);
  if (!transport) {
    return {
      available: false,
      unavailable: {
        transport: id,
        code: TRANSPORT_UNDECLARED,
        missing: `a transport declaration for "${id}"`,
        detail: `${descriptor.id} names a transport the Engine did not declare, so this browser cannot address it.`,
      },
    };
  }
  if (!transport.generic) {
    return {
      available: false,
      unavailable: {
        transport: id,
        code: TRANSPORT_NOT_GENERIC,
        missing: `a generic envelope schema for "${id}"`,
        detail: `${descriptor.id} uses a dedicated ${id}, which the Engine does not mark generic. `
          + "The Engine states that a dedicated route exists for it; this browser has no route and will not invent one.",
      },
    };
  }
  const addressField = addressFieldOf(transport.envelope);
  if (addressField === undefined) {
    return {
      available: false,
      unavailable: {
        transport: id,
        code: TRANSPORT_NOT_ADDRESSABLE,
        missing: `a single required string address property in the "${id}" envelope schema`,
        detail: `${descriptor.id} cannot be addressed because the published ${id} envelope does not say `
          + "which property carries the registered operation. Sending a body anyway would be a guess.",
      },
    };
  }
  const payloadField = payloadFieldOf(transport.envelope, addressField);
  if (payloadField === undefined) {
    return {
      available: false,
      unavailable: {
        transport: id,
        code: TRANSPORT_NOT_ADDRESSABLE,
        missing: `a single object payload property in the "${id}" envelope schema`,
        detail: `${descriptor.id} cannot be addressed because the published ${id} envelope does not say `
          + "which property carries the descriptor's payload.",
      },
    };
  }
  // The envelope says WHERE the address goes; the descriptor must say WHAT it is.
  //
  // A capability the query envelope addresses by `intent` publishes that value as
  // `dispatchIntent`, and the Engine's own validator rejects any such descriptor
  // whose token is not one its route really dispatches on. So an empty token here
  // is a contradiction between two published documents, and the honest outcome is
  // a NAMED unavailability — not a fallback to the capability id, which is the
  // derivation that made every generic read a 400, and not an empty `intent`,
  // which would be sending a value the Engine says means "no token".
  if (descriptor.kind === "query" && descriptor.dispatchIntent === "") {
    return {
      available: false,
      unavailable: {
        transport: id,
        code: TRANSPORT_NO_ADDRESS_VALUE,
        missing: `a dispatchIntent for ${descriptor.id}, which the "${id}" envelope addresses by intent`,
        detail: `${descriptor.id} is a query the ${id} addresses by intent, and it publishes the neutral empty `
          + "dispatchIntent. This browser will not derive an address from the capability id and will not send an "
          + "empty one, so the capability is reported unavailable until the Engine publishes the token its route "
          + "dispatches on.",
      },
    };
  }
  const routes = ROUTES.get(id);
  if (!routes) {
    return {
      available: false,
      unavailable: {
        transport: id,
        code: TRANSPORT_NO_ROUTE,
        missing: `an HTTP route for the generic "${id}" transport`,
        detail: `${descriptor.id} is addressable on the ${id}, but this browser has no route for it and `
          + "refuses to guess one. Until it does, the capability is reported as unavailable rather than hidden.",
      },
    };
  }
  const auxiliaryFields = Object.keys(propertiesOf(transport.envelope))
    .filter(name => name !== addressField && name !== payloadField)
    .sort();
  return {
    available: true,
    plan: Object.freeze({
      transport: id,
      phases: Object.freeze((Object.keys(routes) as ActionPhase[]).filter(phase => routes[phase] !== "")),
      addressField,
      payloadField,
      auxiliaryFields: Object.freeze(auxiliaryFields),
      paths: Object.freeze({ ...routes }),
    }),
  };
}

/**
 * The registered address VALUE for a descriptor.
 *
 * Two published sources, in the order the Engine itself distinguishes them.
 *
 *  1. `dispatchIntent`. When the Engine publishes a non-empty one, that IS the
 *     address, and this browser sends it verbatim. It is not derived, stripped or
 *     normalised: the Engine states in its own contract that there is
 *     deliberately NO uniform "strip the namespace" rule, because a built-in
 *     query dispatches on the bare registered token while an extension query
 *     dispatches on its own namespaced id. One rule that READS the published
 *     value covers both; a rule that DERIVES one would be wrong for half of them
 *     and quiet about it.
 *
 *  2. The descriptor's own `requestSchema`. When it publishes the registered
 *     value under the address field, that value IS the address — a plan
 *     operation's `type`, which is a suffix of the capability id rather than the
 *     id. A plan operation publishes the neutral empty `dispatchIntent`, because
 *     it is not addressed by `intent` at all.
 *
 *     The Engine states that field in either JSON Schema spelling, and both are
 *     read: a single-member `enum`, and a `const`. Reading only `enum` made this
 *     function fall through to the capability id for every plan operation the
 *     Engine publishes as a `const`, so the browser addressed
 *     `operation/atex/legacy-migrate` where the Engine registers
 *     `atex/legacy-migrate` — a mismatch the Engine refuses. The value is READ
 *     either way and is still never DERIVED from the id.
 *
 * The capability id remains the last resort for a descriptor publishing neither,
 * which is what an identity-addressed envelope — a projection addressed by
 * `view` — means by it.
 */
export function addressValue(descriptor: CapabilityDescriptor, addressField: string): string {
  if (descriptor.dispatchIntent !== "") return descriptor.dispatchIntent;
  const properties = propertiesOf(descriptor.requestSchema);
  const published = properties[addressField];
  if (published !== undefined) {
    if (Array.isArray(published.enum) && published.enum.length === 1) {
      const only = published.enum[0];
      if (typeof only === "string" && only.length > 0) return only;
    }
    if (typeof published.const === "string" && published.const.length > 0) return published.const;
  }
  return descriptor.id;
}
